import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Divider, Flex, Skeleton, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { setActiveBookings } from '@/store/app-process/app-process.ts';

import { AppRoutes } from '@/router/routes';

import { EditBooking } from '@/features/EditBooking/EditBooking';
import { NewBooking } from '@/features/NewBooking';

import { bookingApi } from '@/shared/api/booking';
import { isActiveBooking, isNearestBooking } from '@/shared/helpers/utils';
import useAppStore from '@/shared/store/app.store';
import { PersonBookingsObject } from '@/shared/types/booking.types';
import IconChevronRight from '@/shared/uikit/assets/icons/IconChevronRight.svg?react';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { ActiveBookingMore } from './ActiveBookingMore';
import { ActiveBookingSelected } from './ActiveBookingSelected';
import { useAppDispatch } from '@/hooks';

export const ActiveBooking = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { data: bookings, isLoading: isBookingsLoading } = useQuery({
		queryKey: ['bookings'],
		queryFn: bookingApi.getBookingsForClient,
	});
	const isMobile = useAppStore((state) => state.isMobile);

	const [selectedWorkSpace, setSelectedWorkSpace] = useState<PersonBookingsObject>();

	const [openedActiveBooking, { close: closeActiveBooking, open: openActiveBooking }] =
		useDisclosure(false);
	const [openedWorkSpace, { close: closeWorkSpace, open: openWorkSpace }] = useDisclosure(false);
	const [openedActions, { close: closeActions, open: openActions }] = useDisclosure(false);
	const [openedEditing, { close: closeEdit, open: openEdit }] = useDisclosure(false);
	const [openedNewBooking, { close: closeNewBooking, open: openNewBooking }] = useDisclosure(false);

	const handleOpenSelectedWorkSpace = (workSpace: PersonBookingsObject) => {
		if (isMobile) {
			setSelectedWorkSpace(workSpace);
			openWorkSpace();
		} else {
			navigate(`/bookings`);
		}
	};

	const activeBookings = useMemo(() => {
		const filtered = bookings?.filter((item) => {
			if (dayjs().isBetween(dayjs(item.start), dayjs(item.end)) || isNearestBooking(item.start))
				return item;
		});

		if (filtered?.length) return filtered;
	}, [bookings]);

	useEffect(() => {
		if (activeBookings) {
			dispatch(setActiveBookings(activeBookings));
		}
	}, [activeBookings]);

	return (
		<>
			<Skeleton radius={24} visible={isBookingsLoading}>
				<LayoutRow
					type="once"
					title={{
						title: {
							children: 'Активные бронирования',
							size: 'h4',
							fw: 800,
						},
						button: {
							children: `еще`,
							onClick: () => (isMobile ? openActiveBooking() : navigate(AppRoutes.Bookings)),
						},
					}}
				>
					{activeBookings &&
						activeBookings.map((booking) => (
							<Fragment key={booking.id}>
								<Flex
									justify="space-between"
									onClick={() => handleOpenSelectedWorkSpace(booking)}
									style={{ cursor: 'pointer' }}
								>
									<Flex direction="column" maw={200}>
										<Text size="md" fw={600}>
											{booking.booking_object?.name?.replace(/\s\|\s[\d\D]+/gm, '')}
										</Text>
										<Text c="dimmed" size="sm" fw={500}>
											{booking.booking_object.parent?.parent?.name} •{' '}
											{booking.booking_object.parent?.name}
										</Text>
									</Flex>
									<Flex align="center" gap={5}>
										<Flex direction="column" ta={'right'}>
											<Text c="dimmed" size="sm" fw={500}>
												{`С ${dayjs(booking.start).format('HH.mm')} до ${dayjs(booking.end).format('HH.mm')}`}
											</Text>
											<Text c="dimmed" size="sm" fw={500}>
												{dayjs(booking.start).format('DD MMMM')}
											</Text>
										</Flex>
										<IconChevronRight
											color="rgba(102, 112, 133, .5)"
											cursor="pointer"
											fontSize={12}
										/>
									</Flex>
								</Flex>
								{activeBookings[activeBookings.length - 1] !== booking && (
									<Divider my={8} color="#6670851A" />
								)}
							</Fragment>
						))}
				</LayoutRow>
			</Skeleton>

			{bookings && (
				<BottomSheet
					opened={openedActiveBooking}
					onClose={closeActiveBooking}
					title="Активные бронирования"
					withCloseButton={false}
					buttons={[
						{
							children: 'Новое бронирование',
							onClick: openNewBooking,
						},
					]}
					withBackButton
					isFullScreen
				>
					<ActiveBookingMore />
				</BottomSheet>
			)}

			{isMobile && selectedWorkSpace && (
				<>
					<BottomSheet
						opened={openedWorkSpace}
						onClose={closeWorkSpace}
						title={selectedWorkSpace?.booking_object.description}
						buttons={[
							{
								children: isNearestBooking(selectedWorkSpace?.start) ? 'Отменить' : 'Завершить',
								variant: 'default',
								onClick: openActions,
							},
							{ children: 'Редактировать', onClick: openEdit },
						]}
						withBackButton={false}
						isFullScreen
					>
						<ActiveBookingSelected
							booking={selectedWorkSpace}
							actions={{
								type: isActiveBooking(selectedWorkSpace?.start, selectedWorkSpace?.end)
									? 'end'
									: 'cancel',
								opened: openedActions,
								close: closeActions,
								closeParent: closeWorkSpace,
							}}
						/>
					</BottomSheet>
				</>
			)}

			{selectedWorkSpace && openedEditing && (
				<EditBooking id={String(selectedWorkSpace.id)} open={openedEditing} close={closeEdit} />
			)}

			{openedNewBooking && <NewBooking open={openedNewBooking} close={closeNewBooking} />}
		</>
	);
};
